import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Type-ahead lead picker. Replaces long scrollable dropdowns.
 *
 * Props:
 *   value         — currently selected lead_id (string)
 *   onChange      — (lead_id, lead | null) => void
 *   leadsFallback — optional array of leads (e.g. already fetched on page)
 *                   used to resolve the display label when `value` is set
 *                   on first mount.
 *   placeholder   — input placeholder when no lead is picked
 *   testId        — base testid prefix for input + result rows
 */
export default function LeadPicker({
  value,
  onChange,
  leadsFallback = [],
  placeholder = "Type business or contact name…",
  testId = "lead-picker",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selected, setSelected] = useState(null);
  const containerRef = useRef(null);

  // Resolve the initial label when `value` is provided
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?.lead_id === value) return;
    const fromFallback = leadsFallback.find((l) => l.lead_id === value);
    if (fromFallback) {
      setSelected(fromFallback);
      return;
    }
    // Fetch the single lead so we can show its name
    fetch(`${API}/leads/${value}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((lead) => {
        if (lead?.lead_id) setSelected(lead);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (q.length === 0) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API}/email-composer/lead-search?q=${encodeURIComponent(q)}&limit=10`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setResults(data.leads || []);
          setShowResults(true);
        }
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const pick = (lead) => {
    setSelected(lead);
    setQuery("");
    setResults([]);
    setShowResults(false);
    onChange?.(lead.lead_id, lead);
  };

  const clear = () => {
    setSelected(null);
    setQuery("");
    setResults([]);
    onChange?.("", null);
  };

  return (
    <div className="relative" ref={containerRef} data-testid={`${testId}-container`}>
      {selected ? (
        <div className="flex items-center gap-2 p-2 rounded-md border border-blue-200 bg-blue-50" data-testid={`${testId}-selected`}>
          <Badge className="bg-blue-100 text-blue-700 text-[10px]">Lead</Badge>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate">{selected.business_name}</div>
            {(selected.contact_name || selected.city) && (
              <div className="text-xs text-slate-500 truncate">
                {[selected.contact_name, selected.city, selected.state].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-slate-400 hover:text-red-600"
            title="Clear lead"
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <Input
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setShowResults(results.length > 0)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            data-testid={`${testId}-input`}
          />
          {searching && (
            <Loader2 className="absolute right-2 top-2.5 w-4 h-4 text-slate-400 animate-spin" />
          )}
          {showResults && results.length > 0 && (
            <div
              className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
              data-testid={`${testId}-results`}
            >
              {results.map((l) => (
                <button
                  type="button"
                  key={l.lead_id}
                  onClick={() => pick(l)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                  data-testid={`${testId}-result-${l.lead_id}`}
                >
                  <div className="text-sm font-medium text-slate-900">{l.business_name}</div>
                  <div className="text-xs text-slate-500">
                    {[l.contact_name, l.contact_email, l.email, l.city, l.state]
                      .filter(Boolean).join(" · ")}
                  </div>
                </button>
              ))}
            </div>
          )}
          {showResults && !searching && query && results.length === 0 && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg px-3 py-2 text-xs text-slate-400">
              No leads match "{query}"
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-1">
            Leave blank for no lead.
          </p>
        </>
      )}
    </div>
  );
}
